/**
 * ANA-738 (B1 indicator) — the honest presentation of the mic button across its
 * THREE states, plus the lock earcon. The mic auto-locks during `thinking` (B1);
 * the button must show that distinctly from a user's own mute — reusing the muted
 * glyph would read as "you muted yourself" and hide the one-tap override.
 *
 * Pure + framework-free so the tri-state logic is unit-tested without a DOM.
 */

export type MicButtonMode = 'live' | 'muted' | 'locked';

export interface MicButtonPresentation {
  mode: MicButtonMode;
  /** Accessible name — for `locked`, it IS the override instruction. */
  ariaLabel: string;
  /** Present only when the button reflects the USER's own toggle state
   *  (muted/live). Absent for `locked` — that's the system's state, not a toggle. */
  ariaPressed: boolean | undefined;
  title: string;
}

/**
 * Resolve how the mic button should present. Precedence: a USER mute wins over the
 * lock (if they deliberately muted, show muted, not locked — and the hook never
 * locks an already-muted mic, so this is belt-and-braces). Otherwise a `micLocked`
 * shows the distinct locked affordance whose action is "tap to interrupt".
 */
export function micButtonPresentation(muted: boolean, micLocked: boolean): MicButtonPresentation {
  if (muted) {
    return { mode: 'muted', ariaLabel: 'Unmute', ariaPressed: true, title: 'Unmute' };
  }
  if (micLocked) {
    return {
      mode: 'locked',
      ariaLabel: 'Anaka is working — tap the mic to interrupt',
      ariaPressed: undefined,
      title: 'Anaka is working — tap to interrupt',
    };
  }
  return { mode: 'live', ariaLabel: 'Mute', ariaPressed: false, title: 'Mute' };
}

/**
 * The polite screen-reader announcement for the lock state (a visually-hidden
 * `aria-live` region carries this). Empty string when unlocked so the region
 * clears. Eyes-free users (earbuds, walking) otherwise get NO cue that the mic
 * went quiet while Anaka works — the product-UX gap ANA-738 closes.
 */
export function micLockAnnouncement(micLocked: boolean): string {
  return micLocked ? 'Microphone paused while Anaka is working. Tap the mic to interrupt.' : '';
}

/**
 * Play a short, best-effort earcon when the mic lock engages/releases — the
 * established convention for an autonomous mic-state change (every meeting tool
 * ships the mute/unmute pair). A descending pair on lock, ascending on release.
 * Fully guarded: no AudioContext (SSR/tests/blocked autoplay) ⇒ silent no-op; a
 * telemetry/audio failure must never affect the call.
 */
export function playMicLockEarcon(locked: boolean): void {
  try {
    const AC: typeof AudioContext | undefined =
      typeof window !== 'undefined'
        ? window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        : undefined;
    if (!AC) return;
    const ctx = new AC();
    // A live voice call means the user has already gestured, so resume() succeeds;
    // if it's suspended (no gesture yet) we just skip rather than throw.
    if (ctx.state === 'suspended') { void ctx.resume().catch(() => {}); }
    const now = ctx.currentTime;
    // lock: 660→440 (settling down); unlock: 440→660 (opening up).
    const freqs = locked ? [660, 440] : [440, 660];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      const t = now + i * 0.09;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.06, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.09);
    });
    // Close the context shortly after the earcon finishes to free the resource.
    setTimeout(() => { void ctx.close().catch(() => {}); }, 400);
  } catch {
    /* audio unavailable / blocked — the visual + aria-live cue still carry it */
  }
}
